/**
 * In-flight (and recently failed) deck analyses for the signed-in investor.
 *
 * These are the pipeline's "not a company yet" cards. A dealflow row cannot
 * stand in for them: `dealflow.startup_id` is NOT NULL, and the company's
 * identity is what extraction produces — so between upload and extraction there
 * is nothing for a dealflow card to point at. Minting a placeholder
 * `startup_profiles` row named after the FILE would reconcile against the real
 * extracted identity later, which is the mechanism that produced `finai`/`finai-2`
 * and the duplicate `machineMD`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { analyzeInvestorPitchDeck, clearInflightRequest } from '@/lib/supabase/edge-functions';
import { deckMimeType } from './pitch-deck-analysis';
import {
  resolveAnalysisState,
  type AnalysisStatus,
  type ResolvedAnalysisState,
} from './analysis-status';

export interface DeckAnalysisCard {
  id: string;
  fileName: string;
  startupId: string | null;
  dealflowId: string | null;
  createdAt: string;
  state: ResolvedAnalysisState;
}

/** Failed analyses stay visible this long so a failure is not just a gap. */
const FAILURE_VISIBLE_MS = 24 * 60 * 60 * 1000;

export const deckAnalysesQueryKey = ['deck-analyses'] as const;

export function useDeckAnalyses() {
  return useQuery({
    queryKey: deckAnalysesQueryKey,
    queryFn: async (): Promise<DeckAnalysisCard[]> => {
      const { data, error } = await supabase
        .from('deck_analyses')
        .select('id, file_name, status, failure_reason, startup_id, dealflow_id, created_at, updated_at')
        .order('created_at', { ascending: false })
        .limit(25);

      // Bind the error: a silent failure here would render "no analyses
      // running", which is indistinguishable from the bug this feature exists
      // to remove.
      if (error) throw error;

      const now = Date.now();

      return (data ?? [])
        .map((row) => ({
          id: row.id,
          fileName: row.file_name,
          startupId: row.startup_id,
          dealflowId: row.dealflow_id,
          createdAt: row.created_at,
          state: resolveAnalysisState(
            {
              status: row.status as AnalysisStatus,
              updated_at: new Date(row.updated_at).getTime(),
              failure_reason: row.failure_reason,
            },
            now,
          ),
        }))
        .filter((card) => {
          // A finished analysis has a real dealflow card; showing it here too
          // would double it in the pipeline.
          if (card.state.status === 'done') return false;
          if (card.state.status === 'failed') {
            return now - new Date(card.createdAt).getTime() < FAILURE_VISIBLE_MS;
          }
          return true;
        });
    },
    // Poll only while something is actually moving. A background task the
    // client cannot subscribe to needs asking; an idle pipeline does not.
    refetchInterval: (query) => {
      const cards = query.state.data as DeckAnalysisCard[] | undefined;
      const live = cards?.some(
        (c) => c.state.status === 'analysing' || c.state.status === 'scoring',
      );
      return live ? 5_000 : false;
    },
    staleTime: 2_000,
  });
}

/** Copy for each state. Written for someone who does not know what a pass is. */
export function describeAnalysis(state: ResolvedAnalysisState): {
  label: string;
  detail: string;
} {
  switch (state.status) {
    case 'analysing':
      return { label: 'Analysing', detail: 'Reading the deck' };
    case 'scoring':
      return { label: 'Scoring', detail: 'Company found — scoring now' };
    case 'failed':
      if (isClosedCompanyMatch(state)) {
        // Not a failure of the analysis: the deck matches a company that closed
        // on Inverio, and the investor decides. The company's name is only in
        // the notification, so this copy does not need it.
        return { label: 'Matches a closed company', detail: CLOSED_COMPANY_MATCH_COPY };
      }
      return { label: "Couldn't analyse", detail: failureCopy(state.failureReason) };
    default:
      return { label: 'Done', detail: '' };
  }
}

export const CLOSED_COMPANY_MATCH_COPY =
  "This deck matches a company that has closed on Inverio. If it's a different company, confirm to analyse it.";

/** 119-03: the job stopped before any credit was spent, waiting for the investor. */
export function isClosedCompanyMatch(state: ResolvedAnalysisState): boolean {
  return state.status === 'failed' && !state.presumedDead && state.failureReason === 'matched_closed_company';
}

function failureCopy(reason: string | null): string {
  switch (reason) {
    case 'deck_unreadable':
      return "We couldn't open this file. Try exporting it as a PDF and uploading it again.";
    case 'promotion_failed':
      return "We read the deck but couldn't add the company to your pipeline. Please try again.";
    case 'internal_error':
      return 'Something went wrong on our side. Please try again.';
    case 'deck_too_large':
      return 'This deck is too large to analyse. Try a version under 30 MB.';
    case 'malware_detected':
      return 'This file failed our malware scan and was removed.';
    case 'scan_unavailable':
      return 'Our file scanner was unavailable. Please try again shortly.';
    case 'scoring_quota_exhausted':
      return "You've used all your analyses this month.";
    case 'extraction_failed':
      return "We couldn't read this deck. It may be image-only or corrupted.";
    case 'deck_missing':
      return 'The uploaded file could not be found. Please upload it again.';
    case 'timed_out':
      // The staleness rule, not a reported error — the worker died without
      // getting to say so.
      return 'This analysis stopped unexpectedly. Please try again.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

/**
 * "Different company, analyse" on a `matched_closed_company` card (119-03):
 * re-post the SAME analysisId and the row's file name with
 * `confirm_no_conflict: true`. The deck is still in storage under that id, so
 * nothing is uploaded again; the server re-opens only this investor's job that
 * stopped for exactly that reason (202, 'analysing'), then scores the deck on a
 * NEW private profile — never the closed company.
 */
export function useConfirmDeckAnalysisDifferentCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (card: Pick<DeckAnalysisCard, 'id' | 'fileName'>) => {
      // The client dedupes this function by file name; the first post is long
      // finished, but a stale entry would hand back its answer instead of posting.
      clearInflightRequest('investor-analyze-deck', card.fileName);
      const { error } = await analyzeInvestorPitchDeck({
        analysisId: card.id,
        fileName: card.fileName,
        mimeType: deckMimeType(card.fileName),
        confirm_no_conflict: true,
      });
      if (error) throw new Error(error);
    },
    onSettled: () => {
      // Restart polling: the row goes back to 'analysing'.
      queryClient.invalidateQueries({ queryKey: deckAnalysesQueryKey });
    },
  });
}
